import { RotateCcw, UserRoundPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { createVaffangoogle } from "../lib/api";
import type { GeneratedResult, PublicConfig } from "../types";
import { AudioConsole } from "./AudioConsole";
import { VoiceHero } from "./VoiceHero";

type Props = {
  config: PublicConfig;
  checkoutNotice: string;
  onCreditsChange: (credits: number) => void;
  onBuy: () => void;
  onProfile: () => void;
};

export function GeneratorPage({
  config,
  checkoutNotice,
  onCreditsChange,
  onBuy,
  onProfile,
}: Props) {
  const [situation, setSituation] = useState("");
  const [result, setResult] = useState<GeneratedResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [justGenerated, setJustGenerated] = useState(false);
  const trialNeedsProfile =
    !config.profile && config.credits < config.trialInitialCredits;

  useEffect(() => {
    if (!justGenerated || !result) return;
    const timer = window.setTimeout(() => {
      document.getElementById("risultato")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [justGenerated, result]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setJustGenerated(false);
    if (!config.generationReady) {
      setError(
        "La generazione vocale non è disponibile. Nessun VAFFA sarà consumato.",
      );
      return;
    }
    if (trialNeedsProfile) {
      onProfile();
      return;
    }
    if (config.credits < 1) {
      onBuy();
      return;
    }
    try {
      setLoading(true);
      const generated = await createVaffangoogle({
        situation,
      });
      setResult(generated);
      onCreditsChange(generated.credits);
      setJustGenerated(true);
      window.setTimeout(() => setJustGenerated(false), 900);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Generazione non riuscita.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mobile-main">
      {checkoutNotice ? (
        <div className="checkout-notice">{checkoutNotice}</div>
      ) : null}

      {!result ? (
        <VoiceHero>
          <form onSubmit={submit} className="generator-form quick-composer">
            {!config.profile && config.credits > 0 ? (
              <div className="trial-offer" aria-label="Offerta di prova gratuita">
                <span className="trial-offer__number" aria-hidden="true">
                  {config.credits >= 3 ? "3" : config.credits}
                </span>
                <span className="trial-offer__copy">
                  <strong>
                    {config.credits >= 3
                      ? "I primi 3 vocali sono gratis"
                      : `${config.credits} ${config.credits === 1 ? "vocale gratuito rimasto" : "vocali gratuiti rimasti"}`}
                  </strong>
                  <small>
                    {config.credits >= config.trialInitialCredits
                      ? "Il primo subito. Gli altri con il profilo. Nessuna carta."
                      : "Registrati per usarli. Nessuna carta."}
                  </small>
                </span>
              </div>
            ) : null}

            <div className="field-group">
              <div className="label-row">
                <label htmlFor="situation">Cos’è successo?</label>
                <span>{situation.length}/280</span>
              </div>
              <textarea
                id="situation"
                value={situation}
                maxLength={280}
                minLength={10}
                required
                placeholder="Es. Mi ha fatto aspettare quaranta minuti senza avvisare…"
                onChange={(event) => setSituation(event.target.value)}
              />
              <small className="composer-hint">
                Poche parole: al resto pensiamo noi.
              </small>
            </div>

            <button
              className="generate-button"
              type="submit"
              disabled={loading || !config.generationReady}
            >
              <span>
                {loading
                  ? "Sto preparando la voce…"
                  : trialNeedsProfile
                    ? "Registrati per continuare"
                  : config.generationReady
                    ? "Crea il vocale"
                    : "Voce non disponibile"}
                <small>
                  {!config.generationReady
                    ? "Nessun VAFFA sarà consumato"
                    : trialNeedsProfile
                      ? `Altri ${config.credits} gratis · nessuna carta`
                    : !config.profile && config.credits > 0
                    ? "Gratis · MP3 incluso"
                    : "1 VAFFA · MP3 incluso"}
                </small>
              </span>
            </button>
            {error ? <p className="form-error">{error}</p> : null}
          </form>
        </VoiceHero>
      ) : (
        <div className="generator-layout">
          <AudioConsole result={result} justGenerated={justGenerated} />
          {!config.profile && config.credits > 0 ? (
            <button
              className="trial-profile-nudge"
              type="button"
              onClick={onProfile}
            >
              <UserRoundPlus aria-hidden="true" />
              <span>
                <strong>
                  {config.credits === 1
                    ? "Registrati e usa l’ultimo gratis"
                    : `Registrati e usa gli altri ${config.credits} gratis`}
                </strong>
                <small>
                  Bastano email e password
                  <br />
                  Nessuna carta richiesta
                </small>
              </span>
            </button>
          ) : null}
          <button
            className="restart-button"
            type="button"
            onClick={() => {
              setResult(null);
              setSituation("");
              setJustGenerated(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <RotateCcw aria-hidden="true" /> Crea un altro
          </button>
        </div>
      )}
    </main>
  );
}
